import React from "react";
import { Box, Typography, SxProps } from "@mui/material";
import sanitizeHtml from "@/utils/sanitize-html";

// === Strict component – never receives null/undefined headerText ===
interface SectionHeaderStrictProps {
  headerText: string;
  copyText?: string;
  headerTestId?: string;
  copyTestId?: string;
  sx?: SxProps;
  headerSx?: SxProps;
  copySx?: SxProps;
  headerLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

const SectionHeaderStrict: React.FC<SectionHeaderStrictProps> = ({
  headerText,
  copyText,
  headerTestId,
  copyTestId,
  sx,
  headerSx,
  copySx,
  headerLevel = "h2" // Default to h2 if not specified
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: {xs:"1.2rem", md:"2.4rem"},
      alignSelf: "stretch",
      ...sx
    }}
  >
    {headerText && (
      <Typography
        variant={headerLevel || "h2"}
        component={headerLevel || "h2"}
        sx={{
          textAlign: "left",
          alignSelf: "stretch",
          ...headerSx
        }}
        data-testid={headerTestId}
      >
        {headerText}
      </Typography>
    )}

    {copyText && (
      <Typography
        component="div"
        sx={{
          alignSelf: "stretch",
          // Responsive typography styles based on Figma specs
          fontSize: "1.8rem",
          fontWeight: 500,
          lineHeight: "150%", // 27px
          ...copySx
        }}
        data-testid={copyTestId}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(copyText) }}
      />
    )}
  </Box>
);

// === Factory wrapper – accepts possibly null/undefined values ===
export interface SectionHeaderProps {
  headerText?: string | null;
  copyText?: string | null;
  headerTestId?: string;
  copyTestId?: string;
  sx?: SxProps;
  headerSx?: SxProps;
  copySx?: SxProps;
  className?: string;
  headerLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"; 
}

export const SectionHeader: React.FC<SectionHeaderProps> = (props) => {
  const { headerText, copyText, ...rest } = props;

  if (process.env.NODE_ENV !== "production" && !headerText) {
    // eslint-disable-next-line no-console
    console.warn("SectionHeader rendered without headerText", props);
  }

  const strictProps: SectionHeaderStrictProps = {
    headerText: headerText ?? undefined,
    copyText: copyText ?? undefined,
    ...rest
  } as SectionHeaderStrictProps;

  return <SectionHeaderStrict {...strictProps} />;
};

export default SectionHeader;
